import { useMemo } from 'react';

import { useMemberId } from '@trello/authentication';
import {
  useMemberNodeId,
  useOneTimeMessagesDismissed,
} from '@trello/business-logic-react/member';
import { getDateBefore, idToDate } from '@trello/dates';
import { useFeatureGate } from '@trello/feature-gate-client';

import { usePersonalProductivityCohortValue } from 'app/src/components/PersonalProductivityBeta';
import { useIsEligibleForPlannerDiscoverySpotlight } from 'app/src/components/PlannerDiscoverySpotlight';
import { useMemberCampaignsFragment } from './MemberCampaignsFragment.generated';
import { useTrelloPlannerEnabledCalendarsQuery } from './TrelloPlannerEnabledCalendarsQuery.generated';

export const useIsEligibleForOutlookAnnouncement = () => {
  const memberId = useMemberId();
  const memberNodeId = useMemberNodeId();

  // Gate rules deconflict announcement with Planner Discovery experiment
  const { value: isGateEnabled } = useFeatureGate(
    'trello_outlook_announcement',
  );

  const { isOneTimeMessageDismissed } = useOneTimeMessagesDismissed();
  const hasDismissedAnnouncement = isOneTimeMessageDismissed(
    'outlook-announcement-spotlight',
  );

  const personalProductivityCohort = usePersonalProductivityCohortValue();
  const isGA = personalProductivityCohort === 'ga';

  const { isEligibleForExperiment: isExcludedByPlannerDiscoverySpotlight } =
    useIsEligibleForPlannerDiscoverySpotlight();

  const { data: plannerAccountData, loading } =
    useTrelloPlannerEnabledCalendarsQuery({
      variables: { nodeId: memberNodeId },
      skip:
        !isGateEnabled || !memberNodeId || !isGA || hasDismissedAnnouncement,
      waitOn: ['MemberHeader'],
    });

  // In this case the launch screen should appear if Planner is open
  const hasNoConnectedCalendar =
    !loading &&
    plannerAccountData?.trello?.member?.planner?.accounts?.edges?.length === 0;

  /**
   * The user could be a direct signup before the splitscreen campaign was
   * released, but for the needs of identifying users invited within the
   * last two weeks, using the splitscreen campaign to identify direct
   * signup is accurate.
   */
  const { data: memberCampaigns } = useMemberCampaignsFragment({
    from: { id: memberId },
  });
  const isDirectSignup = useMemo(
    () =>
      Boolean(
        (memberCampaigns?.campaigns ?? []).find(
          (c: { name: string }) => c.name === 'splitscreen',
        )?.dateDismissed,
      ),
    [memberCampaigns],
  );
  const canShowOutlookAnnouncementToAnInvitedUser = useMemo(
    () => idToDate(memberId) <= getDateBefore({ days: 14 }),
    [memberId],
  );

  const isEligible =
    isGateEnabled &&
    isGA &&
    !hasDismissedAnnouncement &&
    hasNoConnectedCalendar &&
    (isDirectSignup || canShowOutlookAnnouncementToAnInvitedUser) &&
    !isExcludedByPlannerDiscoverySpotlight;
  return { isEligible };
};
